import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useUser } from "../../context/UserContext";
import { Api } from "../../services/api";
import { renderDescription } from "../../utils/richText";

export default function EventSessions() {
  const { eventId } = useParams();
  const { user } = useUser();
  const api = useMemo(() => new Api(), []);
  const [sessions, setSessions] = useState(null);
  const [sessionVideos, setSessionVideos] = useState({});
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    const handleReload = () => setRefreshKey((k) => k + 1);
    window.addEventListener("reload-live-data", handleReload);
    return () => window.removeEventListener("reload-live-data", handleReload);
  }, []);

  useEffect(() => {
    const getData = async () => {
      const response = await api.getWithAuth(
        `/api/sessions?where[event][equals]=6989a9e9c521905997f219af&sort=startDateTime`,
        user,
      );
      const json = await response.json();
      setSessions(json);
    };
    getData();
  }, [eventId, user, api, refreshKey]);

  useEffect(() => {
    const getVideos = async () => {
      if (!sessions?.docs?.length) {
        return;
      }
      const sessionIds = sessions.docs.map((s) => s.id).join(",");
      const response = await api.getWithAuth(
        `/api/sessionvideos?where[session][in]=${sessionIds}&where[isPublished][equals]=true&limit=100`,
        user,
      );
      const json = await response.json();
      const videoMap = {};
      json.docs.forEach((video) => {
        if (video.session?.id) {
          videoMap[video.session.id] = video;
        }
      });
      setSessionVideos(videoMap);
    };
    getVideos();
  }, [sessions, user, api, refreshKey]);

  return (
    sessions && (
      <div className="col-span-full px-4 py-8">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-3xl font-bold mb-8 text-center">Sessioni</h1>
          <div className="flex flex-col gap-6">
            {sessions.docs.map((session) => (
              <div
                key={session.id}
                className="border rounded-lg p-6 block bg-white"
              >
                <div className="flex items-center mb-2">
                  <h2 className="text-2xl font-semibold session-title">
                    {session.title}
                  </h2>
                  {sessionVideos[session.id]?.status === "live" && (
                    <span className="ml-4 inline-block bg-red-600 text-white text-xs px-2 py-1 rounded animate-pulse">
                      LIVE
                    </span>
                  )}
                </div>
                {session.description && (
                  <div className="text-gray-600 mb-4">
                    {renderDescription(session.description)}
                  </div>
                )}
                <div className="text-sm text-gray-500">
                  <p>
                    <strong>Quando:</strong>{" "}
                    {new Date(session.startDateTime).toLocaleDateString(
                      "it-IT",
                      { weekday: "long", day: "numeric", month: "long" },
                    )}{" "}
                    dalle{" "}
                    {new Date(session.startDateTime).toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}{" "}
                    alle{" "}
                    {new Date(session.endDateTime).toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </p>
                  {session.calendarUrl && (
                    <a
                      href={session.calendarUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-blue-600 hover:underline mt-2"
                    >
                      <span className="mr-1">🗓️</span>
                      <span>Aggiungi al Google Calendar</span>
                    </a>
                  )}
                  {session.calendarFile?.url && (
                    <a
                      href={session.calendarFile.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-blue-600 hover:underline mt-2 sm:ml-4"
                    >
                      <span className="mr-1">🗓️</span>
                      <span>Aggiungi ad Apple / Outlook</span>
                    </a>
                  )}
                </div>
                {sessionVideos[session.id]?.status === "vod" && (
                  <div className="mt-4">
                    <Link
                      to={`/session/${session.id}`}
                      className="inline-block bg-ecweek-secondary text-white px-4 py-2 rounded hover:opacity-90 transition-opacity"
                    >
                      Rivedi la registrazione
                    </Link>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  );
}
